/**
 * The rules for a set of dated landing rates, with no React in sight.
 *
 * Split from `useRateBands.ts` so the arithmetic - month ends, leap years, the
 * overlap check - can be reasoned about and tested without rendering anything.
 * The hook next door is state and callbacks only.
 */

import type { LandingRateBand } from "@/shared/api";

/**
 * One row of the dated-rate editor, held as strings.
 *
 * Strings rather than parsed values because a half-typed rate is a normal state
 * of an input and must not be coerced to `NaN` or silently to zero while
 * somebody is still typing it. Parsing happens once, on submit.
 */
export interface RateBandDraft {
  /** Stable across re-renders, so removing a row does not remount the others. */
  id: string;
  startsOn: string;
  endsOn: string;
  rate: string;
  label: string;
}

/** The outcome of checking a whole set of rows. */
export interface RateBandValidation {
  /** Per-row message, keyed by draft id. Empty when every row is valid. */
  errors: Record<string, string>;
  /** Set when the schedule as a whole is wrong - overlapping spans. */
  scheduleError: string | null;
  /** Parsed bands, or null while anything is invalid. */
  bands: LandingRateBand[] | null;
}

/** Splits a month into halves, which is how freight has actually been re-quoted. */
export const SEMI_MONTHLY = [
  { day: 1, label: "H1" },
  { day: 16, label: "H2" },
] as const;

let nextId = 0;

/** A fresh row. Ids are sequential rather than random so renders stay stable. */
export function emptyDraft(overrides: Partial<RateBandDraft> = {}): RateBandDraft {
  nextId += 1;
  return {
    id: `band-${nextId}`,
    startsOn: "",
    endsOn: "",
    rate: "",
    label: "",
    ...overrides,
  };
}

/**
 * Splits a yyyy-mm-dd string into its parts.
 *
 * Written out rather than destructured off `.map(Number)`, which types every
 * element as possibly undefined and would need three non-null assertions to
 * silence - assertions that would also hide a genuinely malformed value.
 */
function partsOf(iso: string): { year: number; month: number } {
  return { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) };
}

function isoDay(year: number, month: number, day: number): string {
  return new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
}

/** Last day of the month containing `iso`, as yyyy-mm-dd. */
export function endOfMonth(iso: string): string {
  const { year, month } = partsOf(iso);
  // Day 0 of the next month is the last day of this one, and Date handles the
  // December rollover, so this needs no leap-year table.
  return isoDay(year, month + 1, 0);
}

/** The day after `iso`, as yyyy-mm-dd. */
export function nextDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

/** Two rows splitting the month containing `anyDayInMonth` at the 15th. */
export function semiMonthlyDrafts(anyDayInMonth: string): RateBandDraft[] {
  const { year, month } = partsOf(anyDayInMonth);

  return SEMI_MONTHLY.map(({ day, label }, index) => {
    const next = SEMI_MONTHLY.at(index + 1);
    return emptyDraft({
      startsOn: isoDay(year, month, day),
      endsOn: next ? isoDay(year, month, next.day - 1) : endOfMonth(anyDayInMonth),
      label,
    });
  });
}

/**
 * Validates one row on its own terms.
 *
 * @returns A message, or null when the row is usable.
 */
function problemWith(draft: RateBandDraft): string | null {
  if (!draft.startsOn || !draft.endsOn) return "Give the band a start and an end date.";
  if (draft.endsOn < draft.startsOn) return "The end date is before the start date.";

  const rate = Number(draft.rate);
  if (draft.rate.trim() === "" || Number.isNaN(rate)) return "Enter a rate.";
  if (rate <= 0) return "A rate has to be more than zero.";

  return null;
}

/**
 * Checks every row, then the schedule as a whole.
 *
 * Overlaps are caught here as well as on the server. The server has to check -
 * it cannot trust a client - but catching it in the form is what stops somebody
 * uploading two 3 MB workbooks to be told the date they typed was wrong.
 *
 * `bands` is null unless EVERYTHING is valid, so a caller cannot accidentally
 * submit the subset of rows that happen to parse.
 *
 * @example
 * validate([]).bands
 * // => []  (no bands is valid: the run uses one flat rate)
 */
export function validate(drafts: RateBandDraft[]): RateBandValidation {
  const errors: Record<string, string> = {};
  for (const draft of drafts) {
    const problem = problemWith(draft);
    if (problem) errors[draft.id] = problem;
  }

  if (Object.keys(errors).length > 0) {
    return { errors, scheduleError: null, bands: null };
  }

  const bands: LandingRateBand[] = drafts.map((draft) => ({
    starts_on: draft.startsOn,
    ends_on: draft.endsOn,
    rate: Number(draft.rate),
    label: draft.label.trim(),
  }));

  const overlap = firstOverlap(bands);
  if (overlap) {
    return {
      errors,
      scheduleError:
        `Two bands both cover ${overlap}. Every day can take only one rate \u2014 ` +
        `end the earlier band the day before the next one starts.`,
      bands: null,
    };
  }

  return { errors, scheduleError: null, bands };
}

/**
 * The first day claimed by two bands, or null when the spans are disjoint.
 *
 * Adjacent bands do not overlap: the 15th and the 16th touch without colliding.
 */
function firstOverlap(bands: LandingRateBand[]): string | null {
  const ordered = [...bands].sort((a, b) => a.starts_on.localeCompare(b.starts_on));

  for (let index = 1; index < ordered.length; index += 1) {
    const earlier = ordered[index - 1];
    const later = ordered[index];
    if (earlier && later && later.starts_on <= earlier.ends_on) return later.starts_on;
  }

  return null;
}
